import { ClipboardEvent, KeyboardEvent, useEffect, useRef } from 'react';

const DEFAULT_LENGTH = 6;

type VerificationCodeInputProps = {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  length?: number;
  error?: string;
  disabled?: boolean;
  label?: string;
  autoFocus?: boolean;
};

function normalizeCode(raw: string, length: number): string {
  return raw.replace(/\D/g, '').slice(0, length);
}

export default function VerificationCodeInput({
  value,
  onChange,
  onComplete,
  length = DEFAULT_LENGTH,
  error,
  disabled = false,
  label = 'Verification code',
  autoFocus = true,
}: VerificationCodeInputProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const completedForRef = useRef('');
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const digits = Array.from({ length }, (_, index) => value[index] ?? '');

  useEffect(() => {
    if (!autoFocus || disabled) return;
    const index = Math.min(value.length, length - 1);
    inputsRef.current[index]?.focus();
  }, [autoFocus, disabled, length, value.length]);

  useEffect(() => {
    if (value.length === length && completedForRef.current !== value) {
      completedForRef.current = value;
      onCompleteRef.current?.(value);
    }
    if (value.length < length) {
      completedForRef.current = '';
    }
  }, [length, value]);

  function focusIndex(index: number) {
    const next = Math.max(0, Math.min(index, length - 1));
    inputsRef.current[next]?.focus();
    inputsRef.current[next]?.select();
  }

  function updateValue(next: string) {
    const normalized = normalizeCode(next, length);
    onChange(normalized);
    focusIndex(Math.min(normalized.length, length - 1));
  }

  function handleChange(index: number, raw: string) {
    const cleaned = normalizeCode(raw, length);
    if (!cleaned) {
      const chars = digits.slice();
      chars[index] = '';
      onChange(chars.join('').slice(0, index));
      return;
    }
    if (cleaned.length > 1) {
      updateValue(cleaned.length === length || index === 0 ? cleaned : value.slice(0, index) + cleaned);
      return;
    }
    const chars = digits.slice();
    chars[index] = cleaned;
    const joined = chars.join('');
    const compact = joined.slice(0, index) + cleaned + joined.slice(index + 1);
    const normalized = normalizeCode(compact, length);
    onChange(normalized);
    if (index < length - 1) {
      focusIndex(index + 1);
    }
  }

  function handleKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (digits[index]) {
        const chars = digits.slice();
        chars[index] = '';
        onChange(normalizeCode(chars.join('').slice(0, index), length));
        return;
      }
      if (index > 0) {
        const chars = digits.slice();
        chars[index - 1] = '';
        onChange(normalizeCode(chars.join('').slice(0, index - 1), length));
        focusIndex(index - 1);
      }
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusIndex(index - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusIndex(index + 1);
    }
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();
    updateValue(event.clipboardData.getData('text'));
  }

  const fieldId = 'verification-code';

  return (
    <div className="w-full">
      <p id={`${fieldId}-label`} className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </p>
      <div className="flex flex-wrap gap-2" role="group" aria-labelledby={`${fieldId}-label`}>
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(node) => {
              inputsRef.current[index] = node;
            }}
            id={index === 0 ? fieldId : undefined}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            aria-label={`Digit ${index + 1} of ${length}`}
            maxLength={index === 0 ? length : 1}
            value={digit}
            disabled={disabled}
            onChange={(event) => handleChange(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onPaste={handlePaste}
            onFocus={(event) => event.target.select()}
            className={`
              h-11 w-11 sm:h-12 sm:w-12 text-center text-lg font-medium
              border rounded-md text-gray-900
              focus:border-primary focus:ring-2 focus:ring-primary focus:ring-offset-0
              disabled:bg-gray-50 disabled:text-gray-400
              ${error ? 'border-error' : 'border-gray-300'}
            `}
          />
        ))}
      </div>
      {error && (
        <p className="mt-2 text-sm text-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
